import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import CloseButton from './Common/CloseButton';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from './WebSocketContext';
import StorageMentionInput from './Common/StorageMentionInput';
import ActionFlowClarifications from './ProjectDag/ActionFlowClarifications';
import ConnectorPreparationProgress, { ConnectorPreparationSteps, preparationProgressPhase } from './ConnectorPreparationProgress';
import { EMPTY_STORAGE_REFERENCES } from './Common/storageMentionUtils';
import ActionFlowHelp from './ProjectDag/ActionFlowHelp';
import { CREATION_TIMEOUT_MS } from '../Utils/creationTimeouts';

const primaryColor = '#5757b0';
const EMPTY_CLARIFICATION_HISTORY = [];
const creatorAccessErrorKeys = {
  plugin_creation_forbidden: 'connector_creator.creation_access_required',
  plugin_generation_disabled: 'connector_creator.generation_disabled',
  project_access_required: 'connector_creator.project_access_required',
  organization_context_required: 'connector_creator.project_access_required',
};

const progressSteps = [
  { id: 'preparing', threshold: 0.1 },
  { id: 'building', threshold: 0.55 },
  { id: 'checking', threshold: 0.94 },
  { id: 'ready', threshold: 1 },
];

const getProgressStepIndex = (stage) => {
  if (stage === 'preparing') {
    return 0;
  }
  if (stage === 'opencode_generation') {
    return 1;
  }
  if (['validation', 'repairing', 'runtime_testing', 'accepted'].includes(stage)) {
    return 2;
  }
  return stage === 'saved' ? 3 : 0;
};

const getProgressLabel = (stage, t) => {
  const fallbackLabels = {
    preparing: 'Getting things ready',
    opencode_generation: 'Building your action',
    validation: 'Checking that everything works',
    repairing: 'Making a quick correction',
    runtime_testing: 'Doing a final check',
    accepted: 'Everything looks good',
    saved: 'Your action is ready',
  };
  return t(
    `connector_creator.progress_${stage}`,
    fallbackLabels[stage] || 'Preparing your action'
  );
};

const creatorTypeOptions = [
  { value: 'connector', translationKey: 'connector_creator.type_options.connector' },
  { value: 'action', translationKey: 'connector_creator.type_options.action' },
];

const normalizeCreatorType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return ['action', 'connector'].includes(normalized) ? normalized : '';
};

const getConfiguredCreatorType = (value) => normalizeCreatorType(value) || 'connector';

const getPayloadCreatorType = (payload) => {
  const planKindValue = payload?.plan?.kind || payload?.plan?.creator_type;
  const manifestKindValue = payload?.created?.manifest?.kind;
  const planKind = normalizeCreatorType(planKindValue);
  const manifestKind = normalizeCreatorType(manifestKindValue);
  const hasPlanKind = String(planKindValue || '').trim() !== '';
  const hasManifestKind = String(manifestKindValue || '').trim() !== '';

  if ((hasPlanKind && !planKind) || (hasManifestKind && !manifestKind)) {
    return '';
  }
  if (planKind && manifestKind && planKind !== manifestKind) {
    return '';
  }
  return planKind || manifestKind;
};

const getGeneratedPluginFolderName = (pluginInfo) => {
  const explicitFolder = [
    pluginInfo?.folderName,
    pluginInfo?.folder_name,
    pluginInfo?.plugin?.folderName,
  ].find((value) => String(value || '').trim());
  if (explicitFolder) {
    return String(explicitFolder).trim();
  }

  const pathValue = String(pluginInfo?.folder || pluginInfo?.plugin_json || '').trim();
  if (!pathValue) {
    return '';
  }

  const parts = pathValue.split(/[\\/]/).filter(Boolean);
  if (!parts.length) {
    return '';
  }
  const lastPart = parts[parts.length - 1];
  if (/^(plugin|meta)\.(json|py)$/i.test(lastPart) && parts.length > 1) {
    return parts[parts.length - 2];
  }
  return lastPart;
};

const isVerificationFailure = (value) => {
  const message = String(value || '').trim().toLowerCase();
  if (!message) {
    return false;
  }
  return (
    message.includes('plugin creation failed verification')
    || (message.includes('generated plugin') && message.includes('verification'))
    || message.includes('coder verification')
  );
};

const getCreatorCopy = (creatorType, t) => {
  if (creatorType === 'action') {
    return {
      title: t('connector_creator.action_title', 'Create an action'),
      description: t(
        'connector_creator.action_description',
        'Tell us what should happen, using your own words. We will prepare the action for you.'
      ),
      progressTitle: t('connector_creator.progress_title_action', 'Creating your action'),
      createdTitle: t('connector_creator.created_title_action', 'Your action is ready'),
      createdDescription: t(
        'connector_creator.created_description_action',
        'Everything is ready. You can now use your new action.'
      ),
    };
  }

  return {
    title: t('connector_creator.title', 'Connect your data'),
    description: t(
      'connector_creator.description',
      'Tell us which data you want to use. We will prepare the connection for you.'
    ),
    progressTitle: t('connector_creator.progress_title_connector', 'Creating your connection'),
    createdTitle: t('connector_creator.created_title_connector', 'Your connection has been created'),
    createdDescription: t(
      'connector_creator.created_description_connector',
      'Check the source and preview real data before importing.'
    ),
  };
};

const ConnectorCreatorDialog = ({
  open,
  onClose,
  onPluginCreated,
  defaultCreatorType = 'connector',
  lockCreatorType = false,
  deferCloseAfterCreate = false,
  initialRequest = '',
  requestSummary = '',
  initialClarificationHistory = EMPTY_CLARIFICATION_HISTORY,
  initialStorageRefs = EMPTY_STORAGE_REFERENCES,
  caseid = null,
  embedded = false,
  autoPrepare = false,
  onBusyChange,
}) => {
  const { t } = useTranslation();
  const { sendMessage, subscribeToMessages } = useWebSocket();
  const normalizedDefaultCreatorType = getConfiguredCreatorType(defaultCreatorType);
  const [request, setRequest] = useState('');
  const [storageRefs, setStorageRefs] = useState([]);
  const [creatorType, setCreatorType] = useState(normalizedDefaultCreatorType);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isRepairing, setIsRepairing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [accessErrorKey, setAccessErrorKey] = useState('');
  const [resultMessage, setResultMessage] = useState('');
  const [creationProgress, setCreationProgress] = useState({
    stage: 'preparing',
    progress: 0.05,
    attempt: null,
    totalAttempts: null,
    issueCount: null,
  });
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [clarifyingQuestions, setClarifyingQuestions] = useState([]);
  const [questionInputs, setQuestionInputs] = useState([]);
  const questionInputTypes = useMemo(() => new Map(
    questionInputs.map((input) => [input.question_index, input.input_type]),
  ), [questionInputs]);
  const [clarificationAnswers, setClarificationAnswers] = useState({});
  const [clarificationHistory, setClarificationHistory] = useState([]);
  const [createdPluginInfo, setCreatedPluginInfo] = useState(null);
  const [progressUpdates, setProgressUpdates] = useState([]);
  const [progressStartedAt, setProgressStartedAt] = useState(null);
  const recordProgress = useCallback((payload) => {
    const phase = preparationProgressPhase(payload.stage);
    if (!phase) return;
    const entry = { stage: payload.stage, phase, at: Date.now(),
      attempt: Number.isInteger(payload.attempt) && payload.attempt > 0 ? payload.attempt : null };
    setProgressStartedAt((current) => current || entry.at);
    setProgressUpdates((current) => {
      const last = current[current.length - 1];
      return (last?.stage === entry.stage && last?.attempt === entry.attempt
        ? [...current.slice(0, -1), entry] : [...current, entry]).slice(-30);
    });
  }, []);
  const requestSequenceRef = useRef(0);
  const activeRequestIdRef = useRef('');
  const dialogOpenRef = useRef(Boolean(open));
  const creatorTypeRef = useRef(normalizedDefaultCreatorType);
  const dialogSessionRef = useRef('');
  const automaticRepairAttemptedRef = useRef(false);
  const handleAcceptPlanRef = useRef(null);
  const handleGenerateRef = useRef(null);
  const autoStartedRef = useRef(false);
  const autoCreatedPlanRef = useRef(null);
  const activeCreatorType = lockCreatorType
    ? normalizedDefaultCreatorType
    : (normalizeCreatorType(creatorType) || normalizedDefaultCreatorType);
  const dialogSessionKey = `${Boolean(open)}:${normalizedDefaultCreatorType}:${Boolean(lockCreatorType)}`;

  // The provider keeps this dialog mounted between openings. Clear the previous
  // request synchronously when its open/type context changes so a late response
  // cannot be applied to the next creator session.
  dialogOpenRef.current = Boolean(open);
  creatorTypeRef.current = activeCreatorType;
  if (dialogSessionRef.current !== dialogSessionKey) {
    dialogSessionRef.current = dialogSessionKey;
    activeRequestIdRef.current = '';
    automaticRepairAttemptedRef.current = false;
  }

  const creatorCopy = getCreatorCopy(activeCreatorType, t);
  const acceptButtonLabel = activeCreatorType === 'action'
    ? t('connector_creator.accept_action_button', 'Create this step')
    : t('connector_creator.accept_connector_button', 'Create this connection');
  const busy = isGenerating || isCreating || isRepairing;
  const showJourney = autoPrepare && !embedded;
  const journeyTopic = `connector_setup.${activeCreatorType === 'action' ? 'action_creation' : 'creation'}`;
  const visibleRequest = requestSummary || request;
  const showClarifications = clarifyingQuestions.length > 0 && !busy;
  const remainingAnswers = clarifyingQuestions.filter((_question, index) => !String(clarificationAnswers[index] || '').trim()).length;
  const preparationPhase = generatedPlan || isCreating || isRepairing
    ? (['validation', 'repairing', 'runtime_testing', 'accepted', 'saved'].includes(creationProgress.stage) ? 'check' : 'prepare')
    : 'understand';

  const createRequestId = () => {
    requestSequenceRef.current += 1;
    return `plugin-creator-${Date.now()}-${requestSequenceRef.current}`;
  };

  const sendCreatorRequest = (data) => {
    setAccessErrorKey('');
    const sent = sendMessage({ command: 'generatePluginCreator', data: {
      ...data,
      ...(caseid != null ? { caseid } : {}),
    } });
    if (sent === false) {
      activeRequestIdRef.current = '';
      setIsGenerating(false);
      setIsCreating(false);
      setErrorMessage(t('connector_creator.journey.send_failed'));
    }
    return sent;
  };

  useEffect(() => {
    setCreatorType(normalizedDefaultCreatorType);
    setQuestionInputs([]);
    setClarificationHistory(open ? initialClarificationHistory : []);
    if (open) {
      setProgressUpdates([]);
      setProgressStartedAt(null);
      setRequest(String(initialRequest || ''));
      setStorageRefs(initialStorageRefs);
    } else {
      autoStartedRef.current = false;
      autoCreatedPlanRef.current = null;
      setRequest('');
      setStorageRefs([]);
      setIsGenerating(false);
      setIsCreating(false);
      setIsRepairing(false);
      setErrorMessage('');
      setAccessErrorKey('');
      setResultMessage('');
      automaticRepairAttemptedRef.current = false;
      setCreationProgress({
        stage: 'preparing',
        progress: 0.05,
        attempt: null,
        totalAttempts: null,
        issueCount: null,
      });
      setGeneratedPlan(null);
      setShowTechnicalDetails(false);
      setClarifyingQuestions([]);
      setClarificationAnswers({});
      setCreatedPluginInfo(null);
      activeRequestIdRef.current = '';
    }
  }, [initialClarificationHistory, initialRequest, initialStorageRefs, normalizedDefaultCreatorType, open]);

  useEffect(() => {
    if (typeof subscribeToMessages !== 'function') {
      return undefined;
    }
    const unsubscribe = subscribeToMessages(['generatePluginCreator', 'repairGeneratedPlugin'], (message) => {
      const payload = message?.data || {};
      const responseRequestId = String(payload.request_id || payload.requestId || '').trim();
      if (
        !dialogOpenRef.current
        || !activeRequestIdRef.current
        || responseRequestId !== activeRequestIdRef.current
      ) {
        return;
      }

      if (message?.command === 'repairGeneratedPlugin') {
        setIsRepairing(false);
        if (payload.status === 'success') {
          setErrorMessage('');
          setResultMessage(t('connector_creator.repair_success', 'The action was fixed and checked again.'));
          const repairedPlugin = payload.plugin || {};
          setCreatedPluginInfo((current) => ({
            ...(current || {}),
            folderName: payload.folderName || repairedPlugin.folderName || current?.folderName,
          }));
        } else {
          setErrorMessage(t('connector_creator.repair_error', 'We could not fix the action automatically.'));
          setResultMessage('');
        }
        activeRequestIdRef.current = '';
        return;
      }

      if (payload.status === 'processing') {
        recordProgress(payload);
        setIsGenerating(false);
        setIsCreating(true);
        setIsRepairing(false);
        setCreationProgress((current) => ({
          ...current,
          stage: payload.stage || 'preparing',
          progress: Number.isFinite(Number(payload.progress)) ? Number(payload.progress) : current.progress,
        }));
        setErrorMessage('');
        setResultMessage(t('connector_creator.creating_feedback'));
        return;
      }
      if (payload.status === 'progress') {
        const phase = preparationProgressPhase(payload.stage);
        if (!phase) return;
        const planning = phase === 'understand';
        recordProgress(payload);
        setIsGenerating(planning);
        setIsCreating(!planning);
        setIsRepairing(false);
        setCreationProgress((current) => ({
          ...current,
          stage: payload.stage || current.stage,
          progress: Number.isFinite(Number(payload.progress)) ? Number(payload.progress) : current.progress,
          attempt: payload.attempt ?? current.attempt,
          totalAttempts: payload.total_attempts ?? current.totalAttempts,
          issueCount: payload.issue_count ?? current.issueCount,
        }));
        setErrorMessage('');
        return;
      }
      setIsGenerating(false);
      setIsCreating(false);
      setIsRepairing(false);
      if (payload.status === 'error') {
        const accessKey = creatorAccessErrorKeys[payload.code];
        setAccessErrorKey(accessKey || '');
        const planningErrorKeys = {
          INVALID_STORAGE_REFERENCE: 'connector_creator.storage_reference_invalid',
          plan_provider_error: 'connector_creator.plan_provider_error',
          plan_validation_failed: 'connector_creator.plan_validation_failed',
        };
        const planningErrorKey = planningErrorKeys[payload.error_code] || planningErrorKeys[payload.code];
        setErrorMessage(accessKey ? t(accessKey) : planningErrorKey
          ? t(planningErrorKey)
          : (payload.message || t('connector_creator.generate_error')));
        setResultMessage('');
        activeRequestIdRef.current = '';
        return;
      }
      if (payload.status === 'planned') {
        if (getPayloadCreatorType(payload) !== creatorTypeRef.current) {
          setGeneratedPlan(null);
          setErrorMessage(t(
            'connector_creator.type_mismatch',
            'The server returned a different plugin type than the one selected. Please generate the plan again.'
          ));
          setResultMessage('');
          activeRequestIdRef.current = '';
          return;
        }
        if (autoPrepare && (payload.plan?.can_proceed === false || payload.plan?.clarifying_questions?.length > 0)) {
          setErrorMessage(t('connector_creator.plan_validation_failed'));
          activeRequestIdRef.current = '';
          return;
        }
        setGeneratedPlan(payload.plan || null);
        setShowTechnicalDetails(false);
        setClarifyingQuestions([]);
        setClarificationAnswers({});
        setErrorMessage('');
        setResultMessage(t('connector_creator.plan_ready'));
        setCreatedPluginInfo(null);
        activeRequestIdRef.current = '';
        return;
      }
      if (payload.status === 'needs_clarification') {
        const questions = Array.isArray(payload.clarifying_questions)
          ? payload.clarifying_questions.filter((question) => String(question || '').trim())
          : [];
        if (!questions.length) {
          setErrorMessage(t('connector_creator.plan_validation_failed'));
          setResultMessage('');
          activeRequestIdRef.current = '';
          return;
        }
        setGeneratedPlan(null);
        setShowTechnicalDetails(false);
        setClarifyingQuestions(questions);
        setQuestionInputs(Array.isArray(payload.question_inputs) ? payload.question_inputs : []);
        setClarificationAnswers({});
        setErrorMessage('');
        setResultMessage(t('connector_creator.clarification_needed'));
        setCreatedPluginInfo(null);
        activeRequestIdRef.current = '';
        return;
      }
      if (payload.status === 'created') {
        if (getPayloadCreatorType(payload) !== creatorTypeRef.current) {
          setGeneratedPlan(null);
          setCreatedPluginInfo(null);
          setErrorMessage(t(
            'connector_creator.type_mismatch',
            'The server returned a different plugin type than the one selected. Please generate the plan again.'
          ));
          setResultMessage('');
          activeRequestIdRef.current = '';
          return;
        }
        if (autoPrepare && !payload.created?.manifest?.type) {
          setErrorMessage(t('connector_creator.plan_validation_failed'));
          activeRequestIdRef.current = '';
          return;
        }
        setGeneratedPlan(payload.plan || null);
        setClarifyingQuestions([]);
        setClarificationAnswers({});
        setErrorMessage('');
        setResultMessage(creatorTypeRef.current === 'connector'
          ? t('connector_creator.created_description_connector', 'Check the source and preview real data before importing.')
          : t('connector_creator.create_success', 'Your new action is ready to use.'));
        setCreationProgress((current) => ({ ...current, stage: 'saved', progress: 1 }));
        recordProgress({ stage: 'saved' });
        setIsCreating(false);
        setCreatedPluginInfo(payload.created || null);
        if (typeof onPluginCreated === 'function') {
          onPluginCreated(payload, clarificationHistory, { request: requestSummary || request });
        }
        if (!deferCloseAfterCreate && typeof onClose === 'function') {
          onClose();
        }
        activeRequestIdRef.current = '';
        return;
      }
      activeRequestIdRef.current = '';
      if (autoPrepare) {
        setErrorMessage(t('connector_creator.plan_validation_failed'));
        setResultMessage('');
      } else {
        setErrorMessage('');
        setResultMessage(payload.message || t('connector_creator.generate_success'));
      }
    });
    return unsubscribe;
  }, [autoPrepare, clarificationHistory, deferCloseAfterCreate, onClose, onPluginCreated, recordProgress, request, requestSummary, subscribeToMessages, t]);

  useEffect(() => {
    onBusyChange?.(isGenerating || isCreating || isRepairing);
  }, [isCreating, isGenerating, isRepairing, onBusyChange]);

  useEffect(() => {
    if (!autoPrepare || !open || (!isGenerating && !isCreating && !isRepairing)) return undefined;
    const timer = setTimeout(() => {
      activeRequestIdRef.current = '';
      setIsGenerating(false);
      setIsCreating(false);
      setIsRepairing(false);
      setErrorMessage(t('dag.flow_assistant.capability.timeout'));
    }, CREATION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [autoPrepare, isCreating, isGenerating, isRepairing, open, t]);

  const handleGenerate = (answers = {}, history = clarificationHistory) => {
    const trimmedRequest = String(request || '').trim();
    if (busy || activeRequestIdRef.current || !trimmedRequest || typeof sendMessage !== 'function') {
      return;
    }
    const requestId = createRequestId();
    activeRequestIdRef.current = requestId;
    setIsGenerating(true);
    setIsRepairing(false);
    automaticRepairAttemptedRef.current = false;
    setErrorMessage('');
    setResultMessage('');
    setGeneratedPlan(null);
    setShowTechnicalDetails(false);
    const data = {
      mode: 'plan',
      input: trimmedRequest,
      creatorType: activeCreatorType,
      request_id: requestId,
      ...(Object.keys(answers).length > 0 ? { clarificationAnswers: answers } : {}),
      ...(history.length > 0 ? { clarificationHistory: history } : {}),
      ...(storageRefs.length > 0 ? { storage_refs: storageRefs } : {}),
    };
    if (sendCreatorRequest(data) !== false) recordProgress({ stage: 'request_sent' });
  };

  const handleClarificationAnswerChange = (index, value) => {
    setClarificationAnswers((currentAnswers) => ({
      ...currentAnswers,
      [index]: value,
    }));
  };

  const handleSubmitClarifications = () => {
    if (busy || activeRequestIdRef.current) return;
    const missingAnswers = clarifyingQuestions.some(
      (_, index) => !String(clarificationAnswers[index] || '').trim()
    );
    if (missingAnswers) {
      setErrorMessage(t(
        'connector_creator.clarification_answers_required',
        'Answer each question before generating the plan.'
      ));
      return;
    }
    setErrorMessage('');
    const history = [
      ...clarificationHistory.filter((item) => !clarifyingQuestions.includes(item.question)),
      ...clarifyingQuestions.map((question, index) => ({ question, answer: String(clarificationAnswers[index]).trim() })),
    ];
    setClarificationHistory(history);
    handleGenerate(clarificationAnswers, history);
  };

  const handleAcceptPlan = (isRepairRetry = false, previousError = '', preserveError = false) => {
    const trimmedRequest = String(request || '').trim();
    if (
      busy || activeRequestIdRef.current || !trimmedRequest
      || !generatedPlan
      || clarifyingQuestions.length > 0
      || typeof sendMessage !== 'function'
    ) {
      return;
    }
    if (getPayloadCreatorType({ plan: generatedPlan }) !== activeCreatorType) {
      setErrorMessage(t(
        'connector_creator.type_mismatch',
        'The server returned a different plugin type than the one selected. Please generate the plan again.'
      ));
      return;
    }
    const requestId = createRequestId();
    activeRequestIdRef.current = requestId;
    setIsCreating(true);
    setIsRepairing(false);
    const retryError = String(previousError || errorMessage || '').trim();
    setErrorMessage(preserveError ? retryError : '');
    setResultMessage(isRepairRetry
      ? t(
        'connector_creator.repairing_feedback',
        'We are checking the generated action and fixing it if needed. This can take a few minutes.'
      )
      : t('connector_creator.creating_feedback'));
    setCreatedPluginInfo(null);
    setCreationProgress({
      stage: 'preparing',
      progress: 0.05,
      attempt: null,
      totalAttempts: null,
      issueCount: null,
    });
    const repairContext = isRepairRetry && retryError
      ? `\n\nPrevious generation error to repair:\n${retryError}`
      : '';
    const data = {
      mode: 'create',
      input: `${trimmedRequest}${repairContext}`,
      creatorType: activeCreatorType,
      plan: generatedPlan,
      request_id: requestId,
      ...(clarificationHistory.length > 0 ? { clarificationHistory } : {}),
      ...(storageRefs.length > 0 ? { storage_refs: storageRefs } : {}),
    };
    if (sendCreatorRequest(data) !== false) recordProgress({ stage: 'preparing' });
  };

  const handleAutomaticFix = () => {
    const folderName = getGeneratedPluginFolderName(createdPluginInfo);
    if (!folderName) {
      handleAcceptPlan(true);
      return;
    }

    if (typeof sendMessage !== 'function') {
      return;
    }

    const pluginType = String(
      createdPluginInfo?.manifest?.type
      || createdPluginInfo?.plugin?.type
      || generatedPlan?.plugin_type
      || ''
    ).trim();
    const requestId = createRequestId();
    activeRequestIdRef.current = requestId;
    setIsRepairing(true);
    setIsCreating(false);
    setErrorMessage('');
    setResultMessage(t(
      'connector_creator.repairing_feedback',
      'Reviewing and repairing the generated plugin. This can take a few minutes.'
    ));
    sendMessage({
      command: 'repairGeneratedPlugin',
      data: {
        folderName,
        pluginType,
        type: pluginType,
        creatorType: activeCreatorType,
        request_id: requestId,
        error: String(errorMessage || '').trim(),
        message: String(errorMessage || '').trim(),
      },
    });
  };

  handleAcceptPlanRef.current = handleAcceptPlan;
  handleGenerateRef.current = handleGenerate;

  useEffect(() => {
    if (!open || !autoPrepare || autoStartedRef.current || !request.trim()
      || request !== String(initialRequest || '')) return;
    autoStartedRef.current = true;
    handleGenerateRef.current?.();
  }, [autoPrepare, initialRequest, open, request]);

  useEffect(() => {
    if (!open || !autoPrepare || !generatedPlan || autoCreatedPlanRef.current === generatedPlan
      || isGenerating || isCreating || errorMessage || createdPluginInfo) return;
    autoCreatedPlanRef.current = generatedPlan;
    handleAcceptPlanRef.current?.();
  }, [autoPrepare, createdPluginInfo, errorMessage, generatedPlan, isCreating, isGenerating, open]);

  useEffect(() => {
    if (
      !open
      || !generatedPlan
      || !errorMessage
      || isGenerating
      || isCreating
      || automaticRepairAttemptedRef.current
      || !isVerificationFailure(errorMessage)
    ) {
      return;
    }

    automaticRepairAttemptedRef.current = true;
    handleAcceptPlanRef.current?.(true, errorMessage, true);
  }, [errorMessage, generatedPlan, isCreating, isGenerating, open, t]);

  const handleModifyPlan = () => {
    setGeneratedPlan(null);
    setClarifyingQuestions([]);
    setClarificationAnswers({});
    setCreatedPluginInfo(null);
    setErrorMessage('');
    setResultMessage('');
    setIsGenerating(false);
    setIsCreating(false);
    setIsRepairing(false);
    automaticRepairAttemptedRef.current = false;
  };

  const handleDialogClose = (event, reason) => {
    if (isCreating && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    dialogOpenRef.current = false;
    activeRequestIdRef.current = '';
    if (typeof onClose === 'function') {
      onClose(event, reason);
    }
  };

  const Surface = embedded ? Box : Dialog;
  const PreparationActions = embedded ? Stack : DialogActions;
  return (
    <Surface {...(embedded ? { 'aria-busy': isGenerating || isCreating || isRepairing } : {
      open,
      onClose: handleDialogClose,
    })}
      {...(!embedded ? { disableEscapeKeyDown: isCreating, maxWidth: showJourney ? 'sm' : 'md', fullWidth: true,
      PaperProps: {
        sx: {
          ...(showJourney ? { borderRadius: 3, overflow: 'hidden', bgcolor: 'background.paper' } : {
            borderRadius: '18px', background: 'linear-gradient(135deg, #ffffff 0%, #f9f9ff 100%)',
          }),
        }
      } } : {})}
    >
      {!embedded && <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          ...(showJourney ? { px: 3, py: 2, bgcolor: 'background.paper' } : { pb: 1 }),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon sx={{ color: primaryColor }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography component="h2" variant="h6" sx={showJourney ? { color: 'text.primary' } : { color: primaryColor, fontWeight: 700 }}>
                {showJourney ? t(activeCreatorType === 'action' ? `${journeyTopic}.title` : 'connector_creator.journey.title') : creatorCopy.title}
              </Typography>
              {autoPrepare && <ActionFlowHelp topic={t(`${journeyTopic}.title`)}>
                {t(`${journeyTopic}.help`)}
              </ActionFlowHelp>}
              {!showJourney && <Chip
                size="small"
                color="warning"
                label={t('workflow.add_action_menu.labels.beta', 'BETA')}
                sx={{
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  borderRadius: '999px',
                }}
              />}
            </Box>
            {!autoPrepare && <Typography variant="body2" color="text.secondary">
              {t('connector_creator.subtitle', 'Describe the outcome you want')}
            </Typography>}
          </Box>
        </Box>
        <CloseButton onClick={onClose} disabled={isCreating} sx={{ color: 'text.secondary' }} />
      </DialogTitle>}

      {showJourney && <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2, bgcolor: 'background.paper' }}>
        <ConnectorPreparationSteps phase={preparationPhase} creatorType={activeCreatorType} updates={progressUpdates} />
      </Box>}
      <DialogContent dividers={showJourney} sx={showJourney
        ? { bgcolor: 'background.default', px: { xs: 2, sm: 3 }, py: 2.5 }
        : { pt: 1 }}>
        {showJourney && <Stack spacing={2.5}>
          <Box sx={{ p: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="caption" color="text.secondary">{t('connector_setup.your_request')}</Typography>
              {visibleRequest.length > 180 && <ActionFlowHelp topic={t('connector_setup.your_request')}>{visibleRequest}</ActionFlowHelp>}
            </Stack>
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>{visibleRequest}</Typography>
          </Box>
          {busy && <ConnectorPreparationProgress phase={preparationPhase} busy={busy} waiting={showClarifications}
            creatorType={activeCreatorType}
            updates={progressUpdates} startedAt={progressStartedAt}
            error={Boolean(errorMessage) && !busy} reviewingAnswers={isGenerating && clarifyingQuestions.length > 0}
            updateToken={creationProgress} showSteps={false}
            stage={busy ? (isGenerating ? 'planning' : isRepairing ? 'repairing' : creationProgress.stage) : undefined} />}
        </Stack>}
        {!embedded && !autoPrepare && <Alert
          icon={<InfoOutlinedIcon fontSize="small" />}
          severity="info"
          sx={{
            mb: 2,
            borderRadius: '12px',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2">
            {creatorCopy.description}
          </Typography>
        </Alert>}

        {!generatedPlan && (
          <>
            {!lockCreatorType && (
              <TextField
                select
                fullWidth
                margin="normal"
                label={t('connector_creator.type_label', 'What do you want to create?')}
                value={creatorType}
                onChange={(event) => {
                  setCreatorType(event.target.value);
                  setClarifyingQuestions([]);
                  setClarificationAnswers({});
                  setClarificationHistory([]);
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  },
                  '& .MuiInputLabel-outlined.Mui-focused': {
                    color: primaryColor,
                  },
                }}
              >
                {creatorTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Stack component="span" direction="row" alignItems="center" spacing={1}>
                      {option.value === 'connector' ? <HubOutlinedIcon fontSize="small" color="primary" /> : <BoltOutlinedIcon fontSize="small" color="primary" />}
                      <span>{t(option.translationKey)}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
            )}

            {!embedded && !autoPrepare && <StorageMentionInput
              multiline
              minRows={4}
              margin="normal"
              label={t('connector_creator.request_label')}
              placeholder={creatorType === 'action'
                ? t(
                  'connector_creator.action_request_placeholder',
                  'Example: When an event is processed, send a notification to the support team.'
                )
                : t('connector_creator.request_placeholder')}
              value={request}
              disabled={busy}
              onChange={(value) => {
                setRequest(value);
                if (clarifyingQuestions.length > 0) {
                  setClarifyingQuestions([]);
                  setClarificationAnswers({});
                }
              }}
              storageRefs={storageRefs}
              onStorageRefsChange={setStorageRefs}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
                '& .MuiInputLabel-outlined.Mui-focused': {
                  color: primaryColor,
                },
              }}
            />}
            {showClarifications && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t('connector_creator.clarification_title', 'A few details are needed')}
                </Typography>
                <ActionFlowHelp topic={t('connector_creator.clarification_title', 'A few details are needed')}>
                  {t(
                    'connector_creator.clarification_description',
                    'Answer these questions so the plan matches what you want before any preview or plugin is created.'
                  )}
                </ActionFlowHelp>
                </Stack>
                <ActionFlowClarifications questions={clarifyingQuestions} inputTypes={questionInputTypes}
                  answers={clarificationAnswers} caseid={caseid} disabled={busy} multiline answerMaxLength={4000}
                  focusFirstUnanswered={showJourney}
                  onAnswer={handleClarificationAnswerChange} />
              </Stack>
            )}
          </>
        )}

        {errorMessage && (!busy || !showJourney) && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: '12px' }}>
            {showJourney ? <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2">{t(accessErrorKey || 'connector_creator.journey.needs_attention')}</Typography>
              {!accessErrorKey && <ActionFlowHelp topic={t('connector_creator.journey.needs_attention')}>{errorMessage}</ActionFlowHelp>}
            </Stack> : errorMessage}
          </Alert>
        )}

        {resultMessage && !isCreating && !embedded && !autoPrepare && !showClarifications && !errorMessage && (
          <Alert severity={clarifyingQuestions.length > 0 ? 'info' : 'success'} sx={{ mt: 2, borderRadius: '12px' }}>
            {resultMessage}
          </Alert>
        )}

        {embedded && autoPrepare && busy && <Stack spacing={1}>
          <Stack direction="row" spacing={1.5} alignItems="center" role="status" aria-live="polite">
            <CircularProgress size={20} />
            <Typography variant="body2">{t('dag.flow_assistant.capability.preparing')}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">{t('dag.flow_assistant.capability.description')}</Typography>
        </Stack>}
        {!showJourney && !(embedded && autoPrepare) && (embedded || autoPrepare) && isGenerating && <Stack direction="row" spacing={1.5} alignItems="center" role="status" aria-live="polite">
          <CircularProgress size={20} />
          <Typography variant="body2">{t('dag.flow_assistant.capability.preparing')}</Typography>
        </Stack>}
        {isCreating && !showJourney && !(embedded && autoPrepare) && (
          <Alert
            severity="info"
            sx={{
              mt: 2,
              borderRadius: '12px',
              backgroundColor: '#e5f6ff',
              border: '1px solid rgba(33, 150, 243, 0.12)',
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <CircularProgress size={18} thickness={5} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
                  {creatorCopy.progressTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {getProgressLabel(creationProgress.stage, t)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.max(5, Math.min(100, Number(creationProgress.progress || 0) * 100))}
                  sx={{
                    height: 7,
                    borderRadius: '999px',
                    backgroundColor: 'rgba(87, 87, 176, 0.18)',
                    '& .MuiLinearProgress-bar': { borderRadius: '999px' },
                  }}
                />
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 0.75, sm: 1.25 }}
                  sx={{ mt: 1.5 }}
                >
                  {progressSteps.map((step, index) => {
                    const currentStepIndex = getProgressStepIndex(creationProgress.stage);
                    const isCurrent = currentStepIndex === index;
                    const isComplete = currentStepIndex > index
                      || (index === progressSteps.length - 1 && Number(creationProgress.progress || 0) >= step.threshold);
                    return (
                      <Stack
                        key={step.id}
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{
                          minWidth: { sm: 0 },
                          flex: { sm: 1 },
                          color: isComplete ? 'success.main' : isCurrent ? primaryColor : 'text.secondary',
                        }}
                      >
                        {isComplete ? (
                          <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <Box
                            component="span"
                            sx={{
                              width: 16,
                              height: 16,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '50%',
                              border: '1px solid currentColor',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                            }}
                          >
                            {index + 1}
                          </Box>
                        )}
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: isCurrent || isComplete ? 700 : 400 }}
                        >
                          {t(`connector_creator.progress_step_${step.id}`, step.id)}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {t('connector_creator.progress_step_count', 'Step {{current}} of {{total}}', {
                    current: getProgressStepIndex(creationProgress.stage) + 1,
                    total: progressSteps.length,
                  })}
                </Typography>
                {creationProgress.issueCount > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {t(
                      'connector_creator.progress_repairing_notice',
                      'We found a small issue and are fixing it automatically.'
                    )}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Alert>
        )}

        {generatedPlan && !embedded && !autoPrepare && (
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 2,
              borderRadius: '14px',
              border: '1px solid rgba(87, 87, 176, 0.18)',
              backgroundColor: '#f7f7ff',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: primaryColor, mb: 0.5 }}>
              {t('connector_creator.plan_title')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 700 }}>
              {generatedPlan.user_goal || generatedPlan.plan_title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {generatedPlan.summary}
            </Typography>
            {Array.isArray(generatedPlan.user_steps) && generatedPlan.user_steps.length > 0 && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                  {t('connector_creator.steps_label')}
                </Typography>
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {generatedPlan.user_steps.map((step, index) => (
                    <Stack key={`${step}-${index}`} direction="row" spacing={1} alignItems="flex-start">
                      <Box
                        component="span"
                        sx={{
                          flex: '0 0 auto',
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(87, 87, 176, 0.12)',
                          color: primaryColor,
                          fontSize: '0.75rem',
                          fontWeight: 700,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ pt: 0.2 }}>
                        {step}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}
            {Array.isArray(generatedPlan.settings_fields) && generatedPlan.settings_fields.length > 0 ? (
              <>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                  {t('connector_creator.fields_label')}
                </Typography>
                <Stack spacing={0.75}>
                  {generatedPlan.settings_fields.map((field) => (
                    <Typography key={field.key} variant="body2" color="text.secondary">
                      • {field.label || field.key}
                    </Typography>
                  ))}
                </Stack>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t(
                  'connector_creator.no_fields',
                  'You will not need to enter anything else. The action will use the information already available.'
                )}
              </Typography>
            )}
            <Accordion
              disableGutters
              elevation={0}
              expanded={showTechnicalDetails}
              onChange={() => setShowTechnicalDetails((current) => !current)}
              sx={{
                mt: 2,
                border: '1px solid rgba(87, 87, 176, 0.14)',
                borderRadius: '10px !important',
                backgroundColor: 'rgba(255, 255, 255, 0.62)',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <TuneRoundedIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {t('connector_creator.technical_details_label', 'Technical details (optional)')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>{t('connector_creator.technical_plan_label', 'Technical details')}</strong> {generatedPlan.plan_title}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>{t('connector_creator.plugin_name_label', 'Plugin name')}</strong> {generatedPlan.plugin_name}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                  {t('connector_creator.fields_label', 'Settings fields')}
                </Typography>
                {Array.isArray(generatedPlan.settings_fields) && generatedPlan.settings_fields.length > 0 ? (
                  generatedPlan.settings_fields.map((field) => (
                    <Typography key={field.key} variant="body2" color="text.secondary">
                      {field.label} ({field.type}) - {field.key}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      'connector_creator.no_fields',
                      'No document or manual parameter is required. The action will use the current event or project context.'
                    )}
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          </Paper>
        )}

        {createdPluginInfo && !embedded && (
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 2,
              borderRadius: '14px',
              border: '1px solid rgba(56, 142, 60, 0.25)',
              backgroundColor: 'rgba(76, 175, 80, 0.06)',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <CheckCircleOutlineIcon color="success" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {creatorCopy.createdTitle}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {creatorCopy.createdDescription}
            </Typography>
          </Paper>
        )}
      </DialogContent>

      {embedded || autoPrepare ? <PreparationActions sx={embedded
        ? { mt: 2, gap: 1, flexDirection: 'row', justifyContent: 'flex-end' }
        : { p: 2, gap: 1, flexWrap: 'wrap', borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        {showClarifications && <Typography variant="caption" role="status" aria-live="polite" color="text.secondary" sx={{ mr: 'auto', alignSelf: 'center' }}>
          {t(remainingAnswers ? 'connector_creator.journey.answers_remaining' : 'connector_creator.journey.answers_ready', { count: remainingAnswers })}
        </Typography>}
        {!embedded && <Button startIcon={<CloseRoundedIcon />} onClick={onClose} disabled={isCreating}>{t('common.close', 'Close')}</Button>}
        {showClarifications && <Button variant="contained" startIcon={<ArrowForwardRoundedIcon />} onClick={handleSubmitClarifications}
          disabled={isGenerating || isCreating || clarifyingQuestions.some((_, index) => !String(clarificationAnswers[index] || '').trim())}>
          {t('dag.flow_assistant.continue', 'Continue')}
        </Button>}
        {errorMessage && !busy && !showClarifications && <Button variant="contained" startIcon={<ReplayRoundedIcon />}
          onClick={() => generatedPlan ? handleAcceptPlan() : handleGenerate()}>
          {t('dag.flow_assistant.capability.retry')}
        </Button>}
      </PreparationActions> : <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button startIcon={<CloseRoundedIcon />} onClick={onClose} disabled={isCreating} sx={{ textTransform: 'none' }}>
          {t('common.close', 'Close')}
        </Button>
        {!generatedPlan && !busy && (
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={clarifyingQuestions.length > 0
              ? handleSubmitClarifications
              : () => handleGenerate()}
            disabled={isGenerating
              || !String(request || '').trim()
              || (clarifyingQuestions.length > 0
                && clarifyingQuestions.some((_, index) => !String(clarificationAnswers[index] || '').trim()))}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              px: 3,
              background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
            }}
          >
            {isGenerating
              ? t('connector_creator.generating')
              : clarifyingQuestions.length > 0
                ? t('connector_creator.clarification_submit', 'Answer and regenerate plan')
              : t('connector_creator.generate_button', 'Create a plan')}
          </Button>
        )}
        {(createdPluginInfo || (generatedPlan && errorMessage)) && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleAutomaticFix}
            disabled={isCreating || isRepairing}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              px: 3,
            }}
          >
            {isRepairing
              ? t('connector_creator.repairing', 'Fixing...')
              : t('connector_creator.repair_button', 'Fix automatically')}
          </Button>
        )}
        {generatedPlan && !createdPluginInfo && (
          <Button
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            onClick={handleModifyPlan}
            disabled={isCreating}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              px: 3,
            }}
          >
            {t('connector_creator.modify_plan_button')}
          </Button>
        )}
        {generatedPlan && (
          <Button
            variant="contained"
            color="success"
            startIcon={creatorType === 'action' ? <BoltOutlinedIcon /> : <HubOutlinedIcon />}
            onClick={handleAcceptPlan}
            disabled={isCreating || isRepairing}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              px: 3,
            }}
          >
            {isCreating
              ? t('connector_creator.creating')
              : acceptButtonLabel}
          </Button>
        )}
      </DialogActions>}
    </Surface>
  );
};

export default ConnectorCreatorDialog;
